import { buildLogger } from '@instinet-analytics/core-lib';
import { selectActiveWidgetIds } from 'layout/store/selectors';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';
import { selectPortfolioSelection } from 'portfolio/store/selectors';
import without from 'lodash/without';
import { ActionsDispatcher } from 'store/ActionsDispatcher';
import { selectChannelId } from 'streamsState/store/streamStatuses/selectors';
import StreamDataService, {
    StreamDataHandlers,
} from 'streamsState/StreamDataService';
import { noSymbol } from 'widgets/myPortfolioCapsules/store/data/MyPortfolioCapsulesDataAggregator';
import { WidgetCustomStreamManagerSupportingRegisteredSymbolsBase } from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManagerSupportingRegisteredSymbols';
import { WidgetCustomManagedStreamOptions } from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManager';
import uniq from 'lodash/uniq';
import { selectConfiguredSymbolForWidgetOrItsCapsuleContainer } from 'widgets/tiles/store/selectors';
import { selectAppSettings } from 'app/store/appSettings/selectors';
import { SymbolsService } from 'symbols/SymbolsService';
import { HistoricalPercentageChangeConfig } from 'widgets/historicalPercentageChange/store/HistoricalPercentageChangeConfig.ts';
import { HistPriceVolStreamRequest } from 'widgets/historicalPercentageChange/api/HistPriceVolStreamRequest.ts';
import { selectWidgetInstanceConfig } from 'widgets/store/selectors.ts';
import { getHistPriceVolMessageHandler } from 'widgets/historicalPercentageChange/store/data/histPriceVolMessageHandler.ts';
import { HistPriceVolElement } from 'widgets/historicalPercentageChange/api/HistPriceVolElement.ts';
import { toHistPriceVolRequestUid } from 'widgets/historicalPercentageChange/store/data/HistoricalPercentageChangeStreamRequest.ts';
import State from 'store/State.ts';
import { WidgetType } from 'widgets/WidgetType.ts';
import { initialHistoricalPercentageChangeWidgetConfigState } from 'widgets/historicalPercentageChange/widget/store.ts';

/*
 * Custom manager for the HistPriceVol stream
 * If you have a widget asking for 10 days and a capsule asking for 5, its worth one request for the 10,
 * and to get both to use that and display only what they need
 * So we keep track of the max period for each widget
 */
export class HistPriceVolStreamManager extends WidgetCustomStreamManagerSupportingRegisteredSymbolsBase {
    private readonly symbolsService: SymbolsService;
    private readonly streamDataService: StreamDataService;
    private readonly messageHandlers: StreamDataHandlers<HistPriceVolElement>;

    private symbols: string[] = [];
    private maxSymbolPeriodQty: Record<string, number> = {};

    public constructor(options: WidgetCustomManagedStreamOptions) {
        super(
            options.store,
            buildLogger('HistPriceVolStreamManager', options.loggingService)
        );
        this.streamDataService = options.streamDataService;
        this.messageHandlers = getHistPriceVolMessageHandler(
            this.store,
            this.logger
        );
        this.symbolsService = options.dataServices.symbolsService;
    }

    public resetState() {
        this.symbols = [];
    }

    public checkStream(reason: string, dispatcher: ActionsDispatcher): void {
        this.logger.debug('Checking stream', reason);

        this.getActiveSymbolsFromState().then((nextSymbols) => {
            const adding = without(nextSymbols, ...this.symbols);
            const removing = without(this.symbols, ...nextSymbols);

            for (const removedSymbol of removing) {
                this.stopStreaming(removedSymbol, dispatcher, reason);
            }
            this.symbols = nextSymbols;

            for (const symbol of this.symbols) {
                this.startOrUpdateStream(symbol, dispatcher, reason);
            }

            this.logger.debug('Checked', [adding, removing]);
        });
    }

    public getInfo(): Record<string, unknown> {
        return {
            type: 'HistPriceVolStreamManager',
            registeredSymbolStreams: this.registeredSymbolStreams,
            widgetIds: this.widgetIds,
            symbols: this.symbols,
            maxSymbolPeriodQty: this.maxSymbolPeriodQty,
        };
    }

    protected startOrUpdateStream(
        symbol: string,
        dispatcher: ActionsDispatcher,
        reason: string
    ) {
        const periodQty = this.maxSymbolPeriodQty[symbol] ?? 10;

        this.logger.debug('Starting/updating stream', {
            symbol,
            periodQty,
            reason,
        });

        const streamKey = toHistPriceVolRequestUid(symbol);

        const state = this.store.getState();

        const currentChannelId = selectChannelId(state, streamKey);

        const { symbology } = selectAppSettings(state);

        const payload: HistPriceVolStreamRequest = {
            symbol,
            symbology,
            ndays: periodQty,
        };

        (async () => {
            await this.streamDataService.startAndWatchStream(
                streamKey,
                'startHistPriceVolStream',
                this.messageHandlers,
                currentChannelId,
                dispatcher
            )(payload);
        })();
    }

    protected stopStreaming(
        symbol: string,
        dispatcher: ActionsDispatcher,
        reason: string
    ) {
        const stopChannelDestination = 'stopSymbolTilesStream';

        const streamKey = toHistPriceVolRequestUid(symbol);

        this.logger.debug('Stopping streaming ' + stopChannelDestination, {
            reason,
            symbol,
            streamKey,
        });

        // Stop the stream but we keep the channel listener and data to ensure we don't miss messages (as we reuse the channel)...
        this.streamDataService.stopStream(streamKey, dispatcher, true);
    }

    private async getActiveSymbolsFromState(): Promise<string[]> {
        const state = this.store.getState();
        const activeWidgetIds = selectActiveWidgetIds(state);
        const { symbology: usersSymbology } = selectAppSettings(state);

        const activeRegisteredWidgetIds = this.widgetIds.filter(
            (a) => activeWidgetIds.indexOf(a.widgetKey) > -1
        );

        const selection = selectPortfolioSelection(state);
        const selectedSymbol = new PortfolioSelectionWrapper(selection)
            .selectedSymbol;
        // Convert the selected symbol to the users symbology...
        const selectedSymbolInUsersSymbology = selectedSymbol
            ? await this.symbolsService.convertSymbol(
                  selectedSymbol,
                  selection!.symbology,
                  usersSymbology
              )
            : undefined;

        // Configured symbols are by definition in the users symbology...
        const symbols = activeRegisteredWidgetIds
            .map((i) => {
                const configuredSymbol =
                    selectConfiguredSymbolForWidgetOrItsCapsuleContainer(
                        state,
                        i
                    );

                const symbolToUse = configuredSymbol
                    ? configuredSymbol
                    : selectedSymbolInUsersSymbology;

                // Remember the highest period qty...
                const periodQty =
                    selectHistoricalPercentageChangeWidgetInstanceConfig(
                        state,
                        i.widgetType,
                        i.widgetKey
                    ).periodQty;
                this.saveMaxPeriodQty(symbolToUse, periodQty);

                return symbolToUse;
            })
            .filter((s) => !!s) as string[];

        const activeRegisteredSymbolStreamWidgetIds = Object.keys(
            this.registeredSymbolStreams
        ).filter((wk) => activeWidgetIds.indexOf(wk) > -1);

        for (const widgetKey of activeRegisteredSymbolStreamWidgetIds) {
            const widgetSymbols = this.registeredSymbolStreams[
                widgetKey
            ].symbols.filter((s) => s !== noSymbol);

            symbols.push(...widgetSymbols);

            // Remember the highest period qty...
            const periodQty =
                selectHistoricalPercentageChangeWidgetInstanceConfig(
                    state,
                    this.registeredSymbolStreams[widgetKey].widgetType,
                    widgetKey
                ).periodQty;

            for (const widgetSymbol of widgetSymbols) {
                this.saveMaxPeriodQty(widgetSymbol, periodQty);
            }
        }

        this.logger.debug('Registered widget ids are', {
            activeRegisteredWidgetIds,
            activeRegisteredSymbolStreamWidgetIds,
        });

        const ret = uniq(symbols);
        this.logger.debug('Registered symbols are', ret);

        return ret;
    }

    private saveMaxPeriodQty(symbol?: string, periodQty?: number) {
        if (!symbol) {
            return;
        }

        if (!periodQty) {
            return;
        }
        if (
            this.maxSymbolPeriodQty[symbol] &&
            this.maxSymbolPeriodQty[symbol] > periodQty
        ) {
            return;
        }
        this.maxSymbolPeriodQty[symbol] = periodQty;
    }
}

function selectHistoricalPercentageChangeWidgetInstanceConfig(
    state: State,
    widgetType: WidgetType,
    widgetKey: string
): HistoricalPercentageChangeConfig {
    const config = selectWidgetInstanceConfig(state, {
        widgetKey,
        widgetType,
    }) as HistoricalPercentageChangeConfig;

    return config ?? initialHistoricalPercentageChangeWidgetConfigState;
}
